import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * Floating panel positioning for Shopify 2.0 themes.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    if (viewTransition.current) {
      await viewTransition.current;
    }

    // Reset before measuring so old offsets do not stack
    this.style.top = `${OFFSET}px`;
    this.style.left = '0px';
    this.style.right = 'auto';

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    let leftAdjustment = 0;

    // Keep panel inside right edge
    if (rect.right > viewportWidth - OFFSET) {
      leftAdjustment = (viewportWidth - OFFSET) - rect.right;
    }

    // Keep panel inside left edge
    if (rect.left + leftAdjustment < OFFSET) {
      leftAdjustment = OFFSET - rect.left;
    }

    this.style.left = `${leftAdjustment}px`;

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(() => {
    this.#updatePosition();
  });

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';

    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
    }

    this.#updatePosition();
  }, 100);

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, {
        attributes: true,
        attributeFilter: ['open', 'class', 'style']
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}